import { Query, JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { or } from 'patronum';
import {
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
	combineCashiersMultiselectData,
} from 'entities/cashdesk';
import {
	Cashdesk,
	createEditCashdeskMutation,
	createGetCashiersQuery,
	EditCashdeskMutationParams,
	EntityWithName,
} from 'shared/api';
import {
	createModalFormModel,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';
import { MultiSelectItem } from 'shared/ui';

export type EditCashdeskFormValue = {
	name: string;
	cashierIds: number[];
	note: string;
};

export type EditCashdeskModel = ModalFormModel<Cashdesk, EditCashdeskFormValue> & {
	getCashiersQuery: Query<number, EntityWithName[], JsonApiRequestError>;
	editCashdeskMutation: Mutation<EditCashdeskMutationParams, Cashdesk, JsonApiRequestError>;
	$cashiers: Store<MultiSelectItem[]>;
	$pending: Store<boolean>;
};

export const createEditCashdeskModel = (): EditCashdeskModel => {
	const getCashiersQuery = createGetCashiersQuery();

	const editCashdeskMutation = createEditCashdeskMutation();

	const model = createModalFormModel<Cashdesk, EditCashdeskFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(CASHDESK_NAME_MIN_LENGTH),
					maxLengthRule(CASHDESK_NOTE_MAX_LENGTH),
				],
			},
			cashierIds: {
				init: [],
			},
			note: {
				init: '',
				rules: [maxLengthRule(CASHDESK_NOTE_MAX_LENGTH)],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const $cashiers = combineCashiersMultiselectData(
		getCashiersQuery.$data.map((cashiers) => cashiers ?? []),
		model.$modalData.map((cashdesk) => cashdesk?.cashiers ?? []),
	);

	const $pending = or(getCashiersQuery.$pending, editCashdeskMutation.$pending);

	sample({
		clock: model.modalButtonClicked,
		fn: ({ account }) => account.id,
		target: getCashiersQuery.start,
	});

	sample({
		clock: model.modalButtonClicked,
		fn: (cashdesk) => ({
			name: cashdesk.name,
			cashierIds: cashdesk.cashiers.map((cashier) => cashier.id),
			note: cashdesk.note ?? '',
		}),
		target: [model.form.setValues, model.form.setInitialValues],
	});

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			cashdesk: model.$modalData,
		},
		fn: ({ values, cashdesk }) => ({
			id: cashdesk!.id,
			name: values.name,
			cashierIds: values.cashierIds,
			note: values.note || undefined,
		}),
		target: editCashdeskMutation.start,
	});

	chain(editCashdeskMutation.finished.success, model.switcher.disable);

	notify(editCashdeskMutation.finished.success, 'edit-cashdesk.success');

	sample({
		clock: model.modalClosed,
		target: [getCashiersQuery.reset, editCashdeskMutation.reset],
	});

	return {
		getCashiersQuery,
		editCashdeskMutation,
		$pending,
		$cashiers,
		...model,
	};
};
